import { useMemo } from "react";
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis } from "recharts";

export const CHART_COLORS = ["#c2410c", "#0369a1", "#15803d", "#7c3aed", "#be185d", "#a16207", "#0f766e", "#4b5563"];
export const fieldLabel = (f) => f.label || f.prompt.replace(/\s*\(.*\)$/, "").replace(/\?$/, "");

const avg = (vals) => (vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : null);

/** Daily averages + one dot per entry for each 0–10 rating field. Blanks are skipped, never 0. */
export function diarySeries(config, diary) {
  const fields = config.entry_fields.filter((f) => f.type === "rating");
  const days = Array.from({ length: config.days }, (_, i) => i + 1);
  const entries = (n) => diary?.days?.[String(n)]?.entries || [];
  const rows = days.map((n) => Object.fromEntries([["day", n], ...fields.map((f) => [f.code, avg(entries(n).map((e) => e[f.code]).filter((v) => typeof v === "number"))])]));
  const dots = fields.map((f, i) => days.flatMap((n) => entries(n).filter((e) => typeof e[f.code] === "number")
    .map((e) => ({ day: n + (i - (fields.length - 1) / 2) * 0.1, value: e[f.code] }))));
  return { fields, days, rows, dots };
}

export function DiaryTrendChart({ config, diary }) {
  const { fields, days, rows, dots } = useMemo(() => diarySeries(config, diary), [config, diary]);
  if (!fields.length) return null;
  const any = dots.some((d) => d.length);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3" data-testid="diary-trend-chart">
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-600">
        <span className="font-semibold text-zinc-950">Through the week</span>
        {fields.map((f, i) => (
          <span key={f.code} className="inline-flex items-center gap-1.5" data-testid={`diary-trend-legend-${f.code}`}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{fieldLabel(f)}
          </span>
        ))}
        <span className="ml-auto text-zinc-500">Line = daily average · faint dot = one entry · blanks not plotted</span>
      </div>
      {!any ? <p className="py-8 text-center text-sm text-zinc-500" data-testid="diary-trend-empty">No 0–10 ratings logged yet.</p> : (
        <ResponsiveContainer width="100%" height={210}>
          <ComposedChart data={rows} margin={{ top: 6, right: 12, bottom: 0, left: -20 }}>
            <CartesianGrid stroke="#e4e4e7" vertical={false} />
            <XAxis dataKey="day" type="number" domain={[0.5, config.days + 0.5]} ticks={days} tickFormatter={(n) => `Day ${n}`} tick={{ fontSize: 11, fill: "#52525b" }} />
            <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 11, fill: "#71717a" }} />
            <Tooltip labelFormatter={(n) => `Day ${Math.round(n)}`} formatter={(v, name) => [`${v}/10`, name]} />
            {fields.map((f, i) => (
              <Line key={f.code} dataKey={f.code} name={`${fieldLabel(f)} · daily average`} stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    strokeWidth={2.2} dot={{ r: 4, fill: CHART_COLORS[i % CHART_COLORS.length] }} connectNulls isAnimationActive={false} />
            ))}
            {fields.map((f, i) => (
              <Scatter key={`${f.code}-dots`} data={dots[i]} dataKey="value" name={fieldLabel(f)} fill={CHART_COLORS[i % CHART_COLORS.length]}
                       fillOpacity={0.35} isAnimationActive={false} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
